import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from '../../routing';
import { commandCenterApi, formatCurrency, formatCurrencyWhole } from '../../utils/api';
import { formatIsoDayShort } from '../../utils/dateFormat';
import Icon from '../../components/Icon';
import PeriodPicker from '../../components/PeriodPicker';
import ChartTooltip, { useHoverTip } from '../../components/ChartTooltip';
import { PRESETS_MONTHLY, activePresetKey, isMonthAligned, lastNMonthsRange, readPeriodParams, withPeriodParams } from '../../utils/period';
import { usePresetPeriod } from '../../utils/usePresetPeriod';
import { useIsMobile } from '../../utils/useIsMobile';
import { LoadState, formatDate, useRefetchOnFocus } from './parts';
import { ChargeModal, CustomerDetailsModal, HeadcountModal } from './businessModals';
import PaymentBoard from './PaymentBoard';
import {
  PRELAUNCH_NOTE, axisLabelOf, dayAxisLabels, dayLabelOf, isPrelaunch, lastDayOfMonth, monthLabelOf,
  spanLabelOf,
} from './businessMonth';
import { BusinessFigures, CardHelp, DeltaDetail, Fee, HelpNote, Metric } from './businessFigures';

// BE העסק — the only screen in the product that is about PEAK's money rather
// than a restaurant's.
//
// It started life as a third tab on לקוחות and was promoted to its own section
// (owner's call, 01/09/2026). The move is right: every other console screen
// answers "what does a customer need from us?", and this one answers "how is
// the business doing?" — a question you arrive at deliberately.
//
// The screen is in three bands, and the split between them is the point:
//
//   1. PERIOD + FIGURES + TRENDS — everything that moves when you change the
//      date range. This is the reading half.
//   2. The DIRECTORY — who we serve, what they pay per month, when they joined
//      and who manages them. Period-INDEPENDENT by design: it is a list of
//      customers, not a list of this month's invoices, so it does not reshuffle
//      under you when you widen the range.
//   3. Writing, which happens in a dialog behind the row's ⋮ and nowhere else
//      (owner, 01/09/2026: the money should not be on the dashboard). A board
//      you read every week should not put a price one keystroke from being
//      rewritten.
//
// The period control is the product's shared PeriodPicker — the same one on
// /suppliers, /documents and /analysis — so the gesture transfers. It hands
// back ISO DAYS; the server resolves them to whole months, because months are
// the only granularity this data has (crm_charges.month), and echoes back the
// span it actually used so this screen states what it got rather than what was
// asked for.
//
// One distinction runs through all of it: a FEE is what a customer pays per
// month from now on; a CHARGE is what a specific month was billed and whether
// it arrived. Editing the fee never rewrites a recorded month. The server
// enforces it (server/lib/crm.js); this screen labels it, because a figure the
// reader cannot tell apart from an estimate is worse than no figure.
//
// Expenses are the stated next half. Nothing here is stubbed for them on
// purpose — a disabled "הוצאות" tile reads as broken, not as planned.

/**
 * Months before PEAK went live, drawn as "not yet live" rather than as zero.
 *
 * A zero column says "we had no customers / no revenue that month"; the truth
 * is there was no product yet, and a year-long chart that starts with seven
 * empty months reads as seven bad months. `liveFrom` is 'YYYY-MM' from the
 * server (lib/crm.js#PEAK_LIVE_MONTH), which also leaves those months out of
 * every figure — the hatch is how the charts say so.
 */
/** The legend entry, only on a chart that actually shows a pre-launch month. */
const prelaunchLegend = (points, liveFrom) =>
  (points.some(p => isPrelaunch(liveFrom, p.month))
    ? [{ label: 'טרם עלייה לאוויר', tone: 'prelaunch' }]
    : []);

/**
 * The months of the span as bars, with the page's shared hover readout.
 *
 * CSS bars plus ChartTooltip, which is what /analysis's classification bars and
 * the budget panel already are — the same construction and the same dark
 * readout, so someone who learned the hover on one card does not re-learn it
 * here. (recharts is on that page too, but only for the donut, and it is
 * lazy-loaded: ~84KB gzip for an arc. Twelve rectangles do not need it.)
 *
 * Every bar keeps a visible minimum height so a zero month is a column you can
 * hover rather than a gap you have to count past, and the last column — the end
 * of the span — is marked so the row has an anchor.
 *
 * Month points by default. A daily series passes `keyOf`, `labelOf` (the
 * accessible and tooltip name of a point) and `labels` (the axis text, one per
 * point, already thinned) — the drawing is the same, only the axis differs.
 */
function TrendBars({ points, valueOf, format, tone, rowsFor, titleFor, keyOf, labelOf, labels, liveFrom }) {
  const wrapRef = useRef(null);
  const { tip, show, hide } = useHoverTip(wrapRef, { tipHeight: 118 });
  const isMobile = useIsMobile();
  const max = Math.max(...points.map(valueOf), 1);
  const active = tip ? points[tip.i] : null;

  // Twelve labels fit a desktop card; thirty-six do not, and on a phone six is
  // already the limit — twelve months across 350px gives each column 27px, and
  // "אוק׳ 25" in 27px is three characters and an ellipsis. Past the threshold
  // only the columns that open a year keep a label, which is the reading the
  // axis is actually for.
  const daily = !!labels;
  // A daily axis past a month has no room for a figure over every column.
  const dense = points.length > (isMobile ? 6 : (daily ? 31 : 14));
  const nameOf = labelOf || (p => monthLabelOf(p.month));
  const axisText = (p, i) => {
    if (labels) return labels[i];
    const showLabel = !dense || i === 0 || p.month.slice(0, 4) !== points[i - 1].month.slice(0, 4);
    return showLabel ? axisLabelOf(p.month, points[i - 1]?.month) : '';
  };

  return (
    <div className={`cc-trend${dense ? ' is-dense' : ''}${daily ? ' is-days' : ''}`} ref={wrapRef} role="img"
      aria-label={points.map(p => `${nameOf(p)}: ${
        isPrelaunch(liveFrom, keyOf ? keyOf(p) : p.month) ? PRELAUNCH_NOTE : format(valueOf(p))}`).join(', ')}>
      {points.map((p, i) => {
        const v = valueOf(p);
        // keyOf is 'YYYY-MM' or 'YYYY-MM-DD'; either compares correctly against
        // the 'YYYY-MM' go-live month as a string.
        const pre = isPrelaunch(liveFrom, keyOf ? keyOf(p) : p.month);
        return (
          <div key={keyOf ? keyOf(p) : p.month} className={`cc-trend-col${i === points.length - 1 ? ' is-current' : ''}`}>
            <span className="cc-trend-value">{!dense && v && !pre ? format(v) : ''}</span>
            <span
              className={`cc-trend-bar ${pre ? 'is-prelaunch' : `is-${tone}`}`}
              style={{ height: pre ? '100%' : `${Math.max((v / max) * 100, 3)}%` }}
              // Hover only, like the /analysis bars: hover does not exist on
              // touch, and the figures are all reachable through the row's ⋮.
              onMouseEnter={isMobile ? undefined : (e) => show(i, e.currentTarget)}
              onMouseLeave={isMobile ? undefined : hide}
            />
            <span className="cc-trend-label">{axisText(p, i)}</span>
          </div>
        );
      })}
      {active && (
        <ChartTooltip
          tip={tip}
          title={titleFor ? titleFor(active) : nameOf(active)}
          {...(isPrelaunch(liveFrom, keyOf ? keyOf(active) : active.month)
            ? { titleNote: PRELAUNCH_NOTE, rows: [] }
            : { rows: rowsFor(active) })}
        />
      )}
    </div>
  );
}

/**
 * Bars where each column is a WHOLE split into two parts.
 *
 * Two series that sum to the total, not two series side by side: "how many of
 * this month's customers are new" and "how much of this month's billing was
 * paid" are both questions about a proportion of one quantity, and a grouped
 * pair would invite reading them as independent. The part is drawn from the
 * BOTTOM of the column, so the eye reads the filled portion against the whole
 * the way it reads a progress bar.
 */
function StackedBars({ points, totalOf, partOf, format, tone, partTone, rowsFor, liveFrom }) {
  const wrapRef = useRef(null);
  const { tip, show, hide } = useHoverTip(wrapRef, { tipHeight: 118 });
  const isMobile = useIsMobile();
  const max = Math.max(...points.map(totalOf), 1);
  const active = tip ? points[tip.i] : null;
  const dense = points.length > (isMobile ? 6 : 14);

  return (
    <div className={`cc-trend is-stacked${dense ? ' is-dense' : ''}`} ref={wrapRef} role="img"
      aria-label={points.map(p => `${monthLabelOf(p.month)}: ${
        isPrelaunch(liveFrom, p.month) ? PRELAUNCH_NOTE : format(totalOf(p))}`).join(', ')}>
      {points.map((p, i) => {
        const total = totalOf(p);
        const part = partOf(p);
        const pre = isPrelaunch(liveFrom, p.month);
        return (
          <div key={p.month} className={`cc-trend-col${i === points.length - 1 ? ' is-current' : ''}`}>
            <span className="cc-trend-value">{!dense && total && !pre ? format(total) : ''}</span>
            <span
              className={`cc-trend-bar ${pre ? 'is-prelaunch' : `is-${tone}`}`}
              style={{ height: pre ? '100%' : `${Math.max((total / max) * 100, 3)}%` }}
              onMouseEnter={isMobile ? undefined : (e) => show(i, e.currentTarget)}
              onMouseLeave={isMobile ? undefined : hide}
            >
              {part > 0 && !pre && (
                <span className={`cc-trend-part is-${partTone}`}
                  style={{ height: `${total ? (part / total) * 100 : 0}%` }} />
              )}
            </span>
            <span className="cc-trend-label">
              {(!dense || i === 0 || p.month.slice(0, 4) !== points[i - 1].month.slice(0, 4))
                ? axisLabelOf(p.month, points[i - 1]?.month) : ''}
            </span>
          </div>
        );
      })}
      {active && (
        isPrelaunch(liveFrom, active.month)
          ? <ChartTooltip tip={tip} title={monthLabelOf(active.month)} titleNote={PRELAUNCH_NOTE} rows={[]} />
          : <ChartTooltip tip={tip} title={monthLabelOf(active.month)} rows={rowsFor(active)} />
      )}
    </div>
  );
}

/** Names the two parts of a stacked column. A split nobody can name is a
 *  two-tone bar the reader has to guess at. */
function Legend({ items }) {
  return (
    <div className="cc-legend">
      {items.map(it => (
        <span key={it.label} className="cc-legend-item">
          <i className={`cc-legend-dot is-${it.tone}`} /> {it.label}
        </span>
      ))}
    </div>
  );
}

/**
 * A line, for the one series that is a rate rather than a set of magnitudes.
 *
 * Bars are right for "how many customers", "how many documents" — discrete
 * counts you compare one to another. Revenue over months is a flow, and a line
 * says "this is where it is heading" in a way twelve separate rectangles do not.
 *
 * Inline SVG rather than a chart library, for the same reason the bars are CSS:
 * this is a polyline and eleven hover targets. `preserveAspectRatio="none"`
 * stretches the path to the card's width, and `vectorEffect="non-scaling-stroke"`
 * is what stops that stretch from smearing the stroke — without it the line
 * thickens with the card.
 *
 * X is reversed (n-1-i) so the oldest month sits on the RIGHT, matching the RTL
 * bar charts above; an SVG's own coordinates run left-to-right regardless of
 * the document's direction, and a time axis running the other way from the
 * chart beside it is the kind of thing nobody notices and everybody misreads.
 */
function TrendLine({ points, valueOf, format, rowsFor, liveFrom }) {
  const wrapRef = useRef(null);
  const { tip, show, hide } = useHoverTip(wrapRef, { tipHeight: 118 });
  const isMobile = useIsMobile();
  const n = points.length;
  const max = Math.max(...points.map(valueOf), 1);
  const active = tip ? points[tip.i] : null;
  // Same rule as the bars — see TrendBars.
  const dense = n > (isMobile ? 6 : 14);

  // Column CENTRES, not box edges. The dots live in flex columns each 1/n of
  // the width, so a path plotted across 0..n-1 puts its endpoints on the card's
  // edges while the dots sit half a column inside — they agree only in the
  // middle, which is exactly the kind of drift that looks like a rendering
  // glitch and is actually two different coordinate systems.
  const xOf = (i) => (n - 1 - i) + 0.5;
  const yOf = (p) => 100 - (valueOf(p) / max) * 92 - 4;
  // The line starts at the first LIVE month: a pre-launch month has no value
  // to plot, and a line drawn along zero through them reads as a flat business.
  const path = points
    .map((p, i) => (isPrelaunch(liveFrom, p.month) ? null : [p, i]))
    .filter(Boolean)
    .map(([p, i], k) => `${k ? 'L' : 'M'}${xOf(i)},${yOf(p)}`)
    .join(' ');

  return (
    <div className={`cc-line${dense ? ' is-dense' : ''}`} ref={wrapRef}>
      {/* The plotting box, sized once. The SVG and the dot columns are both
          absolute inside IT rather than inside .cc-line, so "100% height" means
          the same 110px to both — an <svg> is a replaced element and will not
          stretch to bare `inset` the way a div does, which is how the dots
          ended up plotted against a different height from their own line. */}
      <div className="cc-line-plot">
        <svg className="cc-line-svg" viewBox={`0 0 ${Math.max(n, 1)} 100`}
          preserveAspectRatio="none" aria-hidden="true" focusable="false">
          <path d={path} fill="none" stroke="var(--peak-green)" strokeWidth="2"
            strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        </svg>
        {/* The dots and the hover targets, in document flow so they inherit RTL
            and the axis labels line up with the bar charts above. */}
        <div className="cc-line-cols"
          role="img"
          aria-label={points.map(p => `${monthLabelOf(p.month)}: ${
            isPrelaunch(liveFrom, p.month) ? PRELAUNCH_NOTE : format(valueOf(p))}`).join(', ')}>
          {points.map((p, i) => (
            <div key={p.month}
              className={`cc-line-col${isPrelaunch(liveFrom, p.month) ? ' is-prelaunch' : ''}`}
              onMouseEnter={isMobile ? undefined : (e) => show(i, e.currentTarget)}
              onMouseLeave={isMobile ? undefined : hide}>
              {!isPrelaunch(liveFrom, p.month) && (
                <span className={`cc-line-dot${tip?.i === i ? ' is-active' : ''}`}
                  style={{ top: `${yOf(p)}%` }} />
              )}
              <span className="cc-line-label">
                {(!dense || i === 0 || p.month.slice(0, 4) !== points[i - 1].month.slice(0, 4))
                  ? axisLabelOf(p.month, points[i - 1]?.month) : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
      {active && (
        isPrelaunch(liveFrom, active.month)
          ? <ChartTooltip tip={tip} title={monthLabelOf(active.month)} titleNote={PRELAUNCH_NOTE} rows={[]} />
          : <ChartTooltip tip={tip} title={monthLabelOf(active.month)} rows={rowsFor(active)} />
      )}
    </div>
  );
}

/**
 * Revenue per customer for the span — who the money actually comes from.
 *
 * Ranked horizontal bars, the same construction as /analysis's classification
 * card, because the question is the same shape: a magnitude per named thing,
 * read from the top.
 *
 * Each bar is the customer's EXPECTED revenue for the span — charged or not,
 * which is the whole point of the widget: a customer whose months were never
 * recorded still owes what they owe, and leaving them out would make this list
 * agree with the ledger while disagreeing with the business. The filled part is
 * what actually arrived, so the gap between the two IS the collection problem,
 * per customer, without a second chart.
 */
function RevenueByCustomer({ rows }) {
  const wrapRef = useRef(null);
  const { tip, show, hide } = useHoverTip(wrapRef, { tipHeight: 118 });
  const isMobile = useIsMobile();
  const max = Math.max(...rows.map(r => r.expected), 1);
  const total = rows.reduce((a, r) => a + r.expected, 0);
  const active = tip ? rows[tip.i] : null;

  return (
    <div className="cc-revlist" ref={wrapRef}>
      {rows.map((r, i) => (
        <div
          key={r.id}
          className="cc-revrow"
          onMouseEnter={isMobile ? undefined : (e) => show(i, e.currentTarget)}
          onMouseLeave={isMobile ? undefined : hide}
        >
          <span className="cc-revrow-name" title={r.name}>{r.name}</span>
          <span className="cc-revrow-track">
            <span className="cc-revrow-expected" style={{ width: `${(r.expected / max) * 100}%` }}>
              <span
                className="cc-revrow-collected"
                style={{ width: `${r.expected ? (r.collected / r.expected) * 100 : 0}%` }}
              />
            </span>
          </span>
          <span className="cc-revrow-amount cc-num">{formatCurrencyWhole(r.expected)}</span>
        </div>
      ))}
      {active && (
        <ChartTooltip
          tip={tip}
          title={active.name}
          titleNote={`${active.months_paid} מתוך ${active.months_billed} חודשים שולמו`}
          rows={[
            { label: 'צפי', value: formatCurrency(active.expected) },
            { label: 'נגבה', value: formatCurrency(active.collected) },
            { label: 'לגבייה', value: formatCurrency(active.outstanding) },
            { label: 'מסך הצפי', value: total ? `${Math.round((active.expected / total) * 100)}%` : '—' },
          ]}
        />
      )}
    </div>
  );
}

/**
 * Is הכנסות לעובד going up or down? The first and last LIVE month of the span,
 * and the change between them. null when the span has fewer than two live
 * months — one point is a value, not a direction.
 */
function rpeDirection(points) {
  const live = points.filter(p => p.revenue_per_employee != null);
  if (live.length < 2) return null;
  const first = live[0];
  const last = live[live.length - 1];
  const pct = first.revenue_per_employee
    ? Math.round(((last.revenue_per_employee - first.revenue_per_employee) / first.revenue_per_employee) * 100)
    : null;
  return { first, last, pct };
}

/**
 * One chart, its own card, with the figures it explains sitting on top of it.
 *
 * Was a single "מגמה" card holding all three charts under one page-level strip
 * of six tiles (owner, 01/09/2026: each graph in its own widget, and נגבה /
 * לגבייה above the נגבה graph). The tiles were the weaker half of that: six
 * numbers in one rail, three of them about money and three about volume, left
 * the reader to work out which figure the chart below was drawn from. A metric
 * standing on its own chart cannot be misread that way.
 *
 * Card anatomy follows /analysis — title, a quiet line under it naming the
 * basis, then the figures and the drawing — so the two screens read as one
 * product even though the console is deliberately a different room (its own
 * .cc-card ground, not the customer app's .card).
 */
function TrendCard({ title, subtitle, metrics, note, help, legend, controls, children }) {
  return (
    <div className="cc-card">
      <div className="cc-trendcard-head">
        <div className="cc-trendcard-heading">
          <h2 className="cc-card-title">{title}</h2>
          {subtitle && <div className="cc-card-sub">{subtitle}</div>}
        </div>
        {controls && <div className="cc-trendcard-controls">{controls}</div>}
        {help && <CardHelp title={title}>{help}</CardHelp>}
      </div>
      {metrics?.length > 0 && (
        <div className="cc-cardmetrics">
          {metrics.map(m => <Metric key={m.label} {...m} />)}
        </div>
      )}
      {legend?.length > 0 && <Legend items={legend} />}
      {children}
      {note && <p className="cc-trend-note">{note}</p>}
    </div>
  );
}

/** 'YYYY-MM-DD' → 'DD/MM/YYYY', the product's date format. */
const ddmmyy = (iso) => (iso ? formatIsoDayShort(iso) : '');

/** Share of a whole as a whole percent, '—' when there is no whole. */
const pctOf = (n, total) => (total ? `${Math.round((n / total) * 100)}%` : '—');

/**
 * What became of the documents uploaded in the span, as ONE bar split into the
 * four things a document can have become — approved (תקין), approved but
 * flagged (לא תקין), still a draft, deleted.
 *
 * One 100% bar rather than four bars side by side: the segments are exact
 * partitions of the upload count (server-enforced), so the question is always
 * "what share", and a bar whose parts add up to the whole says that before a
 * single number is read. Segments too small to hold their count leave it to the
 * legend and the hover.
 */
function DocumentsBreakdownBar({ b }) {
  const wrapRef = useRef(null);
  const { tip, show, hide } = useHoverTip(wrapRef, { tipHeight: 96 });
  const isMobile = useIsMobile();
  const segments = [
    { key: 'valid', label: 'אושרו · תקין', n: b.valid, tone: 'valid' },
    { key: 'invalid', label: 'אושרו · לא תקין', n: b.invalid, tone: 'invalid' },
    { key: 'drafts', label: 'בטיוטה', n: b.drafts, tone: 'draft' },
    { key: 'deleted', label: 'נמחקו', n: b.deleted, tone: 'deleted' },
    { key: 'other', label: 'סטטוס אחר', n: b.other, tone: 'other' },
  ].filter(s => s.n > 0);
  const active = tip ? segments[tip.i] : null;

  if (!b.uploaded) return <div className="cc-empty">לא הועלו מסמכים בטווח הזה</div>;

  return (
    <div className="cc-docsplit" ref={wrapRef}>
      <div className="cc-docsplit-bar" role="img"
        aria-label={segments.map(s => `${s.label}: ${s.n}`).join(', ')}>
        {segments.map((s, i) => (
          <span key={s.key} className={`cc-docsplit-seg is-${s.tone}`}
            style={{ flexGrow: s.n }}
            onMouseEnter={isMobile ? undefined : (e) => show(i, e.currentTarget)}
            onMouseLeave={isMobile ? undefined : hide}>
            {s.n / b.uploaded >= 0.08 ? s.n.toLocaleString('he-IL') : ''}
          </span>
        ))}
      </div>
      <div className="cc-legend cc-docsplit-legend">
        {segments.map(s => (
          <span key={s.key} className="cc-legend-item">
            <i className={`cc-legend-dot is-${s.tone}`} />
            {s.label} <b>{s.n.toLocaleString('he-IL')}</b>
            <span className="cc-muted">({pctOf(s.n, b.uploaded)})</span>
          </span>
        ))}
      </div>
      {active && (
        <ChartTooltip tip={tip} title={active.label}
          rows={[
            { label: 'מסמכים', value: active.n.toLocaleString('he-IL') },
            { label: 'מסך ההעלאות', value: pctOf(active.n, b.uploaded) },
            ...(active.key === 'deleted' && b.deleted_after_approval
              ? [{ label: 'מתוכם אחרי אישור', value: String(b.deleted_after_approval) }]
              : []),
          ]}
        />
      )}
    </div>
  );
}

/**
 * The row's ⋮ — every write on this screen.
 *
 * Anchored to the row and closed by a full-screen transparent overlay, the same
 * construction the credits table's ⋮ uses, so the gesture is one ops already
 * has. Anchored by its INLINE-END edge: it is the last cell of an RTL table and
 * the last control on an RTL card, both of which put it hard against the
 * viewport's left edge.
 */
function RowMenu({ items, label }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="cc-rowmenu">
      <button
        type="button"
        className="cc-rowmenu-btn"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(o => !o)}
      >⋮</button>
      {open && (
        <>
          <div className="cc-rowmenu-overlay" onClick={() => setOpen(false)} />
          <div className="cc-rowmenu-panel" role="menu">
            {items.map(item => (
              <button key={item.label} type="button" className="cc-rowmenu-item" role="menuitem"
                onClick={() => { setOpen(false); item.run(); }}>
                <Icon name={item.icon} size={15} /> {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </span>
  );
}

/**
 * The managers of one restaurant, as one string.
 *
 * Joined with ", " and given the full list on hover, rather than wrapping: this
 * is a directory column, and a cell that grows to three lines because one
 * customer has three managers re-rules the whole table for everyone else.
 */
function Managers({ emails }) {
  if (!emails?.length) return <span className="cc-muted">—</span>;
  const all = emails.join(', ');
  return <span className="cc-managers" title={all}>{all}</span>;
}

const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** A `?from=&to=` range from the URL, or null when absent or malformed — so a
 *  link (מבט על's מצב העסק card opens last month) can choose the period, and a
 *  hand-edited URL falls back to the default instead of querying nonsense.
 *  A preset travels as `?period=<key>` instead and is re-evaluated to today, so
 *  a refresh next month doesn't bring back this month's dates (period.js). */
function rangeFromParams(params) {
  const { from, to } = readPeriodParams(params, PRESETS_MONTHLY);
  return ISO_DAY_RE.test(from || '') && ISO_DAY_RE.test(to || '') && from <= to ? { from, to } : null;
}

export default function BusinessPage() {
  // ISO days, because that is what PeriodPicker speaks. The server resolves
  // them to months and tells us which ones it used.
  //
  // Twelve months, not the current one that every other filter bar defaults to.
  // This screen leads with two trend charts, and a one-month default draws them
  // as a single bar — a "trend" with nothing to compare against. The month
  // presets are one click away for the reconciliation view.
  // The URL's range when a link supplied one, else twelve months. Kept in the
  // URL as the picker moves (replace, not push — a period change is not a page
  // the back button should step through), so a refresh keeps the period.
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useState(() => rangeFromParams(params) || lastNMonthsRange(12));
  const changeRange = (from, to) => {
    setRange({ from, to });
    // Called only with a range computed just now (the picker, the widening
    // below, or the roll), so matching it against today's presets is sound.
    setParams(withPeriodParams(new URLSearchParams(), activePresetKey(from, to, PRESETS_MONTHLY), from, to), { replace: true });
  };
  // Rolls a preset forward when the tab comes back after the month changed.
  usePresetPeriod(range.from, range.to, PRESETS_MONTHLY, changeRange);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  // { kind: 'details' | 'charge', customer } — the row editor that is open.
  const [editing, setEditing] = useState(null);
  // The מסמכים שהועלו card's x-axis: 'month' or 'day'. Local to that card —
  // every other chart here is monthly by nature.
  const [docsGrain, setDocsGrain] = useState('month');
  const isMobile = useIsMobile();

  // Switching the period (or the picker's own prev/next steppers) fires a new
  // request before the previous one has landed. Without this token, a slow
  // response for a period the user already left can resolve after the fast
  // one for the period they're now looking at and repaint the page with the
  // wrong range's data — including a legitimately-empty older period silently
  // clobbering a populated one. Mirrors SupplierDocumentsPage / RevenueVsExpensesReportPage.
  const loadToken = useRef(0);
  const load = useCallback(async () => {
    const token = ++loadToken.current;
    setError(false);
    try {
      const r = await commandCenterApi.getCrm(range.from, range.to);
      if (token !== loadToken.current) return;   // superseded
      setData(r.data);
    } catch {
      if (token !== loadToken.current) return;
      setError(true);
      setData(null);
    }
  }, [range.from, range.to]);

  useEffect(() => { load(); }, [load]);
  useRefetchOnFocus(load);

  const summary = data?.summary;
  const customers = useMemo(() => data?.customers || [], [data]);
  const months = useMemo(() => data?.months || [], [data]);
  const revenueRows = useMemo(() => data?.revenue_by_customer || [], [data]);
  const days = useMemo(() => data?.days || [], [data]);
  const liveFrom = data?.live_from || null;
  const docsBreakdown = data?.documents_breakdown || null;
  const prevPrelaunch = !!data && isPrelaunch(liveFrom, data.prev_to);
  // The span reaches back before deletions were recorded, so its deleted count
  // (and therefore its upload count) is a floor. Compared on the month: the span
  // is whole months, and the first tracked month is itself only partly covered.
  const deletionsUntracked = !!docsBreakdown?.deletions_tracked_from && !!data
    && data.from <= docsBreakdown.deletions_tracked_from.slice(0, 7);
  const dayLabels = useMemo(
    () => dayAxisLabels(days.map(d => d.day), isMobile
      ? { everyDay: 7, monthLimit: 6 }
      : { everyDay: 31, monthLimit: 14 }),
    [days, isMobile],
  );

  // The span the SERVER used, which is the one the figures describe. A part
  // month at either end counts as its whole month, so echoing the picker's own
  // dates here would caption the figures with a period they don't cover.
  const spanLabel = data ? spanLabelOf(data.from, data.to) : '';

  const prevLabel = data ? spanLabelOf(data.prev_from, data.prev_to) : '';

  // The month a new charge defaults to: the end of the span, which is the most
  // recent month the reader is looking at.
  const defaultChargeMonth = data?.to;

  // One definition for both layouts — the phone and the desktop must not drift
  // into offering different actions on the same row.
  const menuItems = (c) => {
    const items = [
      { label: 'עריכת פרטי לקוח', icon: 'edit',
        run: () => setEditing({ kind: 'details', customer: c }) },
      // No month in the label: the editor opens on the end of the span but the
      // month is a field inside it, and a label naming one month would read as
      // "this item edits September" — the opposite of the point.
      { label: 'רישום תשלום / חיוב', icon: 'coins',
        run: () => setEditing({ kind: 'charge', customer: c }) },
    ];
    // Offered only when there is one: a menu item that opens about:blank is
    // worse than an absent menu item.
    if (c.folder_url) {
      items.push({
        label: 'פתיחת תיקיית הלקוח', icon: 'external',
        // noopener: the folder is a third-party host and must not get a handle
        // on the console's window.
        run: () => window.open(c.folder_url, '_blank', 'noopener'),
      });
    }
    return items;
  };

  // A readout per card, not one shared set. The customers chart carrying four
  // money rows was the tooltip answering a question its own bars do not ask
  // (owner, 03/09/2026) — and a hover that reports things the chart is not
  // about is how a reader ends up attributing one card's figure to another.
  const customerRows = (m) => [
    { label: 'לקוחות', value: String(m.customers) },
    { label: 'מתוכם חדשים', value: String(m.new_customers) },
    { label: 'מסמכים', value: String(m.documents) },
    { label: 'מסמכים ללקוח', value: m.customers ? (m.documents / m.customers).toFixed(1) : '—' },
  ];

  const moneyRows = (m) => [
    { label: 'הכנסות', value: formatCurrency(m.expected) },
    { label: 'שולם', value: formatCurrency(m.collected) },
    { label: 'לגבייה', value: formatCurrency(m.expected - m.collected) },
    { label: 'תשלומים', value: `${m.paid_count} מתוך ${m.billed}` },
  ];

  const rpeRows = (m) => [
    { label: 'הכנסות לעובד', value: formatCurrency(m.revenue_per_employee ?? 0) },
    { label: 'הכנסות', value: formatCurrency(m.expected) },
    { label: 'עובדים', value: String(m.employees ?? '—') },
  ];
  const rpeTrend = rpeDirection(months);

  const documentRows = (m) => [
    { label: 'מסמכים', value: String(m.documents) },
    { label: 'לקוחות', value: String(m.customers) },
    { label: 'מסמכים ללקוח', value: m.customers ? (m.documents / m.customers).toFixed(1) : '—' },
  ];

  return (
    <div className="cc-business">
      <h1 className="cc-page-title">BE העסק</h1>

      <div className="cc-card">
        <div className="cc-card-head" style={{ marginBottom: 0, flexWrap: 'wrap' }}>
          {/* No heading echoing the range: the picker beside it already prints
              the same words, and the same period twice in one bar reads as two
              settings that might disagree.
              The one case where it is NOT the same words earns a line — the day
              calendar can pick a part-month, and this data is monthly, so the
              figures then cover more than what the picker says. Silent for every
              month-aligned range, which is all of the presets. */}
          {data && !isMonthAligned(range.from, range.to) && (
            <div className="cc-card-sub" style={{ marginTop: 0 }}>
              הנתונים מחושבים לפי חודשים מלאים: {spanLabel}
            </div>
          )}
          <div className="cc-spacer" />
          {/* The product's shared period control, month presets — this data is
              monthly, so the analytic "30 ימים אחרונים" set would offer spans
              this screen cannot honour. */}
          {/* The SAME pill at every width (owner, 03/09/2026). PeriodPicker's
              'bar' variant exists for filter bars where the date is page scope
              on its own sticky line; here the control already has a card to
              itself, so the phone got a full-width navigator with ‹ › steppers
              where the desktop got a pill — one control behaving two ways for
              no reason the reader can see. */}
          <PeriodPicker
            dateFrom={range.from}
            dateTo={range.to}
            onChange={changeRange}
            presets={PRESETS_MONTHLY}
            variant="pill"
          />
        </div>
      </div>

      <LoadState
        loading={data === null && !error}
        error={error}
        onRetry={load}
        empty={!!data && customers.length === 0}
        emptyMessage="אין לקוחות בטווח הזה"
      >
        {summary && (
          <>
            <BusinessFigures data={data} showPlanned onEditHeadcount={() => setEditing({ kind: 'headcount' })} />

            {/* לקוחות beside the revenue line: how many we serve, and what that
                is worth, on one shared x-axis. In RTL the first child sits on
                the RIGHT. */}
            <div className="cc-duo">
              <TrendCard
                title="לקוחות"
                subtitle="מספר הלקוחות בכל חודש · לא כולל חשבון דמו"
                legend={[
                  { label: 'לקוחות קיימים', tone: 'cust' },
                  { label: 'חדשים החודש', tone: 'custnew' },
                  ...prelaunchLegend(months, liveFrom),
                ]}
                help={
                  <>
                    <HelpNote>
                      גובה העמודה הוא מספר הלקוחות <b>בסוף אותו חודש</b> — כל מסעדה שנוצרה
                      עד אותו חודש, כולל, נספרת בו ובכל חודש שאחריו.
                    </HelpNote>
                    <HelpNote>
                      החלק הכהה הוא <b>לקוחות חדשים</b>: מי שההתחלה שלו חלה באותו חודש עצמו —
                      תאריך היצירה של המסעדה במערכת, אלא אם נקבע לה <b>חודש התחלה</b> מפורש
                      בכרטיס הלקוח.
                    </HelpNote>
                    <HelpNote>
                      חשבונות דמו לא נספרים בכלל. מסעדה מסומנת כדמו בכרטיס הלקוח שלה.
                    </HelpNote>
                  </>
                }
              >
                <StackedBars points={months} tone="cust" partTone="custnew" rowsFor={customerRows} liveFrom={liveFrom}
                  totalOf={p => p.customers} partOf={p => p.new_customers}
                  format={v => String(v)} />
              </TrendCard>

              <TrendCard
                title="מגמת הכנסות"
                subtitle="סה״כ הכנסות בכל חודש"
                legend={prelaunchLegend(months, liveFrom)}
                help={
                  <>
                    <HelpNote>
                      הקו מציג את <b>סה״כ ההכנסות</b> בכל חודש — הסכום שהלקוחות אמורים לשלם
                      עליו, בין אם כבר שולם ובין אם לא.
                    </HelpNote>
                    <HelpNote>
                      לחודש שנרשם בו חיוב נלקח <b>הסכום שנרשם</b>. לחודש שלא נרשם בו חיוב
                      נלקח <b>התשלום החודשי הנוכחי</b> של הלקוח, כהערכה — ולכן שינוי מחיר
                      היום לא משנה חודשים שכבר נרשמו.
                    </HelpNote>
                    <HelpNote>
                      לקוח נספר רק מהחודש שבו נוצר במערכת.
                    </HelpNote>
                  </>
                }
              >
                <TrendLine points={months} rowsFor={moneyRows} liveFrom={liveFrom}
                  valueOf={p => p.expected} format={formatCurrencyWhole} />
              </TrendCard>
            </div>

            {/* The star metric over time (owner, 25/09/2026): the tile above
                says what it is; this says whether it is going up or down. */}
            <TrendCard
              title="מגמת הכנסות לעובד בחודש"
              subtitle="סה״כ ההכנסות בכל חודש חלקי מספר העובדים באותו חודש"
              legend={prelaunchLegend(months, liveFrom)}
              metrics={rpeTrend ? [{
                label: `שינוי מ${monthLabelOf(rpeTrend.first.month)} עד ${monthLabelOf(rpeTrend.last.month)}`,
                tone: rpeTrend.pct > 0 ? 'good' : (rpeTrend.pct < 0 ? 'alert' : undefined),
                value: rpeTrend.pct == null ? '—' : `${rpeTrend.pct > 0 ? '+' : ''}${rpeTrend.pct}%`,
                detail: `מ-${formatCurrencyWhole(rpeTrend.first.revenue_per_employee)} ל-${formatCurrencyWhole(rpeTrend.last.revenue_per_employee)} לעובד`,
              }] : []}
              help={
                <>
                  <HelpNote>
                    כל נקודה היא <b>הכנסות לעובד</b> באותו חודש: סה״כ ההכנסות של החודש (אותו
                    מספר כמו בגרף "מגמת הכנסות") חלקי <b>מספר העובדים</b> שהוזן לאותו חודש.
                    חודש שלא הוזן בו מספר נחשב 2 עובדים.
                  </HelpNote>
                  <HelpNote>
                    קו שעולה אומר שההכנסות גדלות מהר יותר מהצוות. קו שיורד — גייסנו לפני
                    שההכנסות הגיעו, או שהלקוחות דורשים יותר עבודה מאיתנו.
                  </HelpNote>
                  <HelpNote>
                    <b>השינוי</b> הוא בין החודש הראשון לחודש האחרון בטווח שיש בהם נתון.
                    חודשים לפני עליית PEAK לא מוצגים.
                  </HelpNote>
                </>
              }
            >
              <TrendLine points={months} rowsFor={rpeRows} liveFrom={liveFrom}
                valueOf={p => p.revenue_per_employee ?? 0} format={formatCurrencyWhole} />
            </TrendCard>

            {/* A PAIR, because they answer the same question from two
                directions: how collection is going month by month, and which
                customers it is going well or badly with. */}
            <div className="cc-duo">
              <TrendCard
                title="סטטוס גבייה"
                subtitle="פיננסי"
                metrics={[
                  {
                    label: 'סה״כ שולם',
                    tone: 'good',
                    value: formatCurrencyWhole(summary.collected),
                    detail: `${summary.paid_count} תשלומים שהתקבלו`,
                  },
                  {
                    label: 'סה״כ לגבייה',
                    tone: summary.outstanding > 0 ? 'alert' : undefined,
                    value: formatCurrencyWhole(summary.outstanding),
                    detail: summary.unpaid_count ? `${summary.unpaid_count} חיובים` : 'הכול נגבה',
                    // Which customers, not just how many. The directory below is
                    // period-independent by design, so without this the answer to
                    // "who owes me?" would mean opening every row.
                    title: summary.unpaid_names?.length
                      ? `ממתינים לתשלום: ${summary.unpaid_names.join(', ')}`
                      : undefined,
                  },
                ]}
                legend={[
                  { label: 'לגבייה', tone: 'due' },
                  { label: 'שולם', tone: 'paid' },
                  ...prelaunchLegend(months, liveFrom),
                ]}
                help={
                  <>
                    <HelpNote>
                      גובה העמודה הוא <b>הסכום לחיוב</b> באותו חודש — מה שהלקוחות אמורים
                      לשלם. החלק הירוק הוא מה ש<b>כבר שולם</b>, והשארית היא מה שנשאר לגבות.
                    </HelpNote>
                    <HelpNote>
                      חודש נחשב כשולם רק אחרי שסומן ידנית ברשימת הלקוחות, בתפריט השורה,
                      תחת "רישום תשלום / חיוב". חודש שלא סומן מופיע כולו
                      כ<b>לגבייה</b>, גם אם הכסף התקבל.
                    </HelpNote>
                    <HelpNote>
                      לחודש שלא נרשם בו חיוב הסכום הוא <b>הערכה</b> לפי התשלום החודשי הנוכחי
                      של הלקוח.
                    </HelpNote>
                  </>
                }
                note={
                  summary.estimated_pairs > 0
                    // How much of the figures above is a projection rather than a
                    // record. It matters most on a HISTORICAL span: nothing was
                    // billed back then, so the amount there is today's price list
                    // applied backwards — a number worth reading, and a number
                    // nobody should quote.
                    ? (summary.estimated_pairs === 1
                      ? 'חיוב אחד בטווח הוא הערכה לפי התשלום החודשי הנוכחי — עדיין לא נרשם לו חיוב.'
                      : `${summary.estimated_pairs} חיובים בטווח הם הערכה לפי התשלום החודשי הנוכחי — עדיין לא נרשם להם חיוב.`)
                    : null
                }
              >
                <StackedBars points={months} tone="due" partTone="paid" rowsFor={moneyRows} liveFrom={liveFrom}
                  totalOf={p => p.expected} partOf={p => p.collected}
                  format={formatCurrencyWhole} />
              </TrendCard>

              <div className="cc-card">
                <div className="cc-trendcard-head">
                  <div className="cc-trendcard-heading">
                    <h2 className="cc-card-title">הכנסות לפי לקוח</h2>
                    <div className="cc-card-sub">
                      הסכום לתקופה לכל לקוח — נרשם חיוב או לא · החלק הכהה הוא מה שנגבה
                    </div>
                  </div>
                  <CardHelp title="הכנסות לפי לקוח">
                    <HelpNote>
                      אורך הפס הוא <b>סך ההכנסות מהלקוח בטווח שנבחר</b> — סכום כל החודשים
                      בטווח שהלקוח קיים בהם, בין אם נרשם להם חיוב ובין אם לא.
                    </HelpNote>
                    <HelpNote>
                      החלק הכהה הוא מה ש<b>כבר שולם</b>. הרשימה ממוינת מהגדול לקטן; לקוח
                      שאין לו לא חיוב ולא תשלום בטווח אינו מופיע בה.
                    </HelpNote>
                  </CardHelp>
                </div>
                {revenueRows.length === 0
                  ? <div className="cc-empty">אין חיובים בטווח הזה</div>
                  : <RevenueByCustomer rows={revenueRows} />}
              </div>
            </div>

            {/* On the SAME x-axis as לקוחות above, which is how you compare two
                series measured in different units. Drawing documents and
                customers into one chart would need two y-scales, and a dual-axis
                chart is the classic way to make two unrelated lines look
                correlated — the scales get chosen until they do. Small multiples
                make the same comparison and cannot lie about it. */}
            <TrendCard
              title="מסמכים שהועלו"
              subtitle="לפי תאריך ההעלאה למערכת · לא כולל חשבון דמו"
              legend={prelaunchLegend(months, liveFrom)}
              controls={
                <select
                  className="cc-grain-select"
                  aria-label="קיבוץ לפי"
                  value={docsGrain}
                  onChange={e => setDocsGrain(e.target.value)}
                >
                  <option value="month">לפי חודשים</option>
                  <option value="day">לפי ימים</option>
                </select>
              }
              help={
                <>
                  <HelpNote>
                    כל מסמך נספר לפי <b>תאריך ההעלאה שלו למערכת</b>, לא לפי התאריך שמופיע
                    על המסמך — השאלה כאן היא כמה הלקוחות משתמשים בנו, וזו שאלה של העלאה.
                  </HelpNote>
                  <HelpNote>
                    <b>מסמכים ללקוח</b> = סך המסמכים בטווח חלקי מספר הלקוחות בסוף הטווח.
                  </HelpNote>
                  <HelpNote>
                    בתצוגה <b>לפי ימים</b> כל עמודה היא יום אחד בטווח, כולל ימים בלי העלאות.
                    היום נקבע לפי שעון UTC, כך שמסמך שהועלה אחרי חצות שעון ישראל עשוי להיספר ביום הקודם.
                  </HelpNote>
                  <HelpNote>
                    ההשוואה היא מול <b>אותו מספר חודשים</b> שקדמו לטווח שנבחר, כדי שהיא
                    תהיה תקפה בכל אורך טווח. חשבונות דמו לא נספרים.
                  </HelpNote>
                </>
              }
              metrics={[
                {
                  label: 'מסמכים',
                  value: summary.documents_total.toLocaleString('he-IL'),
                  detail: <DeltaDetail
                    now={summary.documents_total}
                    prev={summary.documents_prev}
                    prevLabel={prevLabel}
                    prevPrelaunch={prevPrelaunch}
                  />,
                },
                {
                  label: 'מסמכים ללקוח',
                  value: summary.documents_per_customer.toFixed(1),
                  detail: `ל-${summary.customer_count} לקוחות`,
                  title: 'סך המסמכים בטווח, חלקי מספר הלקוחות בסוף הטווח',
                },
              ]}
            >
              {/* Keyed by grain: both branches are TrendBars in the same slot, so
                  without a key React reuses one instance and its hover index
                  would point into the other series. */}
              {docsGrain === 'day' ? (
                <TrendBars key="day" points={days} tone="doc" labels={dayLabels} liveFrom={liveFrom}
                  keyOf={p => p.day} labelOf={p => dayLabelOf(p.day)}
                  rowsFor={p => [{ label: 'מסמכים', value: String(p.documents) }]}
                  valueOf={p => p.documents} format={v => String(v)} />
              ) : (
                <TrendBars key="month" points={months} tone="doc" rowsFor={documentRows} liveFrom={liveFrom}
                  valueOf={p => p.documents} format={v => String(v)} />
              )}
            </TrendCard>

            {docsBreakdown && (
              <TrendCard
                title="פירוט מסמכים שהועלו"
                subtitle="מה קרה למסמכים שהועלו בטווח · לפי תאריך ההעלאה · לא כולל חשבון דמו"
                metrics={[
                  {
                    label: 'הועלו',
                    value: docsBreakdown.uploaded.toLocaleString('he-IL'),
                    // A copy the system set aside at intake (#307) is the
                    // same document sent twice — not an upload (owner, 21/10/2026).
                    detail: 'כולל מסמכים שנמחקו מאז'
                      + (docsBreakdown.duplicates_set_aside
                        ? ` · בלי ${docsBreakdown.duplicates_set_aside.toLocaleString('he-IL')} עותקים כפולים`
                        : ''),
                  },
                  {
                    label: 'אושרו',
                    tone: 'good',
                    value: docsBreakdown.approved.toLocaleString('he-IL'),
                    detail: `${pctOf(docsBreakdown.approved, docsBreakdown.uploaded)} מההעלאות`
                      + (docsBreakdown.invalid ? ` · ${docsBreakdown.invalid} לא תקינים` : ''),
                  },
                  // No "לא אושרו" figure: it would only ever be בטיוטה + נמחקו
                  // (owner, 14/09/2026), and a sum printed beside its own parts
                  // is one more number to read that says nothing new.
                  {
                    label: 'בטיוטה',
                    value: docsBreakdown.drafts.toLocaleString('he-IL'),
                    detail: `${pctOf(docsBreakdown.drafts, docsBreakdown.uploaded)} מההעלאות · ממתינים לאישור`,
                  },
                  {
                    label: 'נמחקו',
                    value: docsBreakdown.deleted.toLocaleString('he-IL'),
                    detail: `${pctOf(docsBreakdown.deleted, docsBreakdown.uploaded)} מההעלאות`,
                    title: docsBreakdown.deleted_after_approval
                      ? `${docsBreakdown.deleted_after_approval} מהם נמחקו אחרי שכבר אושרו`
                      : undefined,
                  },
                ]}
                help={
                  <>
                    <HelpNote>
                      כל מסמך נספר לפי <b>תאריך ההעלאה</b> שלו, ובקטגוריה שבה הוא נמצא <b>היום</b> —
                      כך שאותו טווח יכול להיראות אחרת מחר, כשטיוטות יאושרו.
                    </HelpNote>
                    <HelpNote>
                      <b>אושרו</b> = יצאו משלב הטיוטה: תקין, או <b>לא תקין</b> — מסמך שאושר ויש בו
                      משהו פתוח (זיכוי, או מרכזת שלא מתיישבת מול התעודות). לא תקין אינו "נדחה".
                    </HelpNote>
                    <HelpNote>
                      <b>נמחקו</b> נספרים בנפרד מכל השאר, גם אם נמחקו אחרי אישור (למשל כפילות) —
                      הם כבר לא במערכת. כמה מהם אושרו לפני המחיקה מופיע במעבר עכבר על "נמחקו".
                      הועלו = אושרו + בטיוטה + נמחקו.
                    </HelpNote>
                    <HelpNote>
                      <b>עותקים כפולים</b> — אותו מסמך שנשלח פעמיים (מייל וגם וואטסאפ) והמערכת העבירה
                      למחוקים כבר בקליטה — אינם נספרים ב"הועלו" ולא ב"נמחקו". מספרם מופיע לצד "הועלו".
                    </HelpNote>
                    <HelpNote>
                      לכן <b>הועלו</b> כאן גדול מהמספר בכרטיס "מסמכים שהועלו", שסופר רק מסמכים שעדיין קיימים.
                      קבצים שהועלו לחילוץ ולא נשמרו כמסמך לא נספרים בשום מקום.
                    </HelpNote>
                  </>
                }
                note={
                  deletionsUntracked
                    ? `מחיקות נרשמות רק מ-${ddmmyy(docsBreakdown.deletions_tracked_from)}. מסמך שנמחק לפני כן לא הותיר רשומה, ולכן אינו נספר — גם לא ב"הועלו".`
                    : null
                }
              >
                <DocumentsBreakdownBar b={docsBreakdown} />
              </TrendCard>
            )}

          </>
        )}

        {/* The year board sits directly above the directory: same customers,
            but this one is the WORKING view — you come here to fix a month —
            while the directory below is the reference. */}
        <PaymentBoard />

        <div className="cc-card">
          {/* Same header anatomy as every other card on this page — title, then
              the qualifier UNDER it. It was a single flex line with the note
              beside the title, which put a 0.78rem aside on the same baseline as
              a 1rem heading and made this one card read as a different species
              from the five above it. */}
          <div className="cc-trendcard-head">
            <div className="cc-trendcard-heading">
              <h2 className="cc-card-title">לקוחות</h2>
              {/* Says what the list is NOT, because the band above it moves with
                  the period and this one deliberately does not. */}
              <div className="cc-card-sub">כל הלקוחות — לא תלוי בטווח התאריכים</div>
            </div>
            <CardHelp title="לקוחות">
              <HelpNote>
                הרשימה הזו <b>אינה תלויה בטווח התאריכים</b> — היא עונה על "את מי אנחנו
                משרתים", ולכן לא משתנה כשמזיזים את התקופה למעלה.
              </HelpNote>
              <HelpNote>
                <b>חיוב לחודש</b> הוא התשלום החודשי הנוכחי של הלקוח, זה שיחול על חודשים
                שעדיין לא נרשם בהם חיוב. <b>בעלים ומנהלים</b> הם המשתמשים בתפקיד בעלים / מטה
                או מנהל שמשויכים למסעדה; אדמינים לא מופיעים כאן כי הם מגיעים לכל המסעדות.
              </HelpNote>
              <HelpNote>
                לקוח שסומן לו <b>חודש סיום</b> מוצג כאן עם התווית "עזב" ונשאר ברשימה — הפרטים
                והתיקייה שלו עדיין נחוצים — אבל <b>מהחודש שאחרי הסיום הוא לא נספר באף מדד</b>
                בעמוד: לא בכמות הלקוחות, לא בהכנסות, לא בגבייה ולא בלוח השנתי.
              </HelpNote>
              <HelpNote>
                עמודת <b>הצטרפו</b> מציגה כברירת מחדל את תאריך היצירה של המסעדה במערכת. אפשר
                לדרוס זאת עם <b>חודש התחלה</b> בכרטיס הלקוח — למשל לקוח שהיה שלנו לפני שהמסעדה
                נוצרה במערכת, או שהצטרף בפועל רק מאוחר יותר. לקוח עם דריסה מוצג עם התווית
                "התחיל" ותאריך היצירה המקורי מופיע ב-tooltip. <b>מהחודש שלפני ההתחלה הוא לא
                נספר באף מדד</b>, בדיוק כמו אחרי חודש הסיום.
              </HelpNote>
              <HelpNote>
                עריכת תשלום, איש קשר, קישור לתיקייה ורישום חיוב חודשי — בתפריט ⋮ שבסוף כל שורה.
              </HelpNote>
            </CardHelp>
          </div>
          {isMobile ? (
            <div className="m-list">
              {customers.map(c => (
                <div key={c.id} className="cc-crm-card">
                  <div className="cc-crm-card-head">
                    <span className="cc-crm-name">
                      {c.name}
                      {c.started_month && (
                        <span className="cc-pill" style={{ marginInlineStart: 6 }}
                          title={`ברירת המחדל של המערכת הייתה ${formatDate(c.created_at)}`}>
                          התחיל · {monthLabelOf(c.started_month)}
                        </span>
                      )}
                      {c.ended_month && (
                        <span className="cc-pill bad" style={{ marginInlineStart: 6 }}>
                          עזב · {monthLabelOf(c.ended_month)}
                        </span>
                      )}
                      {c.folder_url && (
                        <a className="cc-folder-link" href={c.folder_url}
                          target="_blank" rel="noopener noreferrer"
                          aria-label={`תיקיית הלקוח של ${c.name}`}>
                          <Icon name="folder" size={13} />
                        </a>
                      )}
                    </span>
                    <RowMenu items={menuItems(c)} label={`פעולות עבור ${c.name}`} />
                  </div>
                  <div className="cc-crm-card-row">
                    <span className="cc-muted">חיוב לחודש</span>
                    <Fee amount={c.monthly_fee} entered={c.monthly_fee_entered} />
                  </div>
                  <div className="cc-crm-card-row">
                    <span className="cc-muted">הצטרפו</span>
                    <span className="cc-num">
                      {c.started_month ? monthLabelOf(c.started_month) : formatDate(c.created_at)}
                    </span>
                  </div>
                  <div className="cc-crm-card-row">
                    <span className="cc-muted">בעלים ומנהלים</span>
                    <Managers emails={c.managers} />
                  </div>
                  {c.contact_name && (
                    <div className="cc-crm-card-row">
                      <span className="cc-muted">איש קשר</span>
                      <span>{c.contact_name}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>לקוח</th>
                    <th>חיוב לחודש</th>
                    <th>הצטרפו</th>
                    <th>בעלים ומנהלים</th>
                    <th>איש קשר</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {customers.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>
                        {c.name}
                        {/* The folder is one click from the name it belongs to,
                            not buried in the ⋮ alone — it is the thing you open
                            while looking at the row, not a thing you edit. */}
                        {c.folder_url && (
                          <a className="cc-folder-link" href={c.folder_url}
                            target="_blank" rel="noopener noreferrer"
                            title="תיקיית הלקוח" aria-label={`תיקיית הלקוח של ${c.name}`}>
                            <Icon name="folder" size={13} />
                          </a>
                        )}
                        {c.started_month && (
                          <span className="cc-pill" style={{ marginInlineStart: 6 }}
                            title={`ברירת המחדל של המערכת הייתה ${formatDate(c.created_at)}`}>
                            התחיל · {monthLabelOf(c.started_month)}
                          </span>
                        )}
                        {c.ended_month && (
                          <span className="cc-pill bad" style={{ marginInlineStart: 6 }}
                            title={`לא נספר באף מדד מ${monthLabelOf(c.ended_month)} ואילך`}>
                            עזב · {monthLabelOf(c.ended_month)}
                          </span>
                        )}
                        {c.network_name && (
                          <span className="cc-pill" style={{ marginInlineStart: 6 }}>{c.network_name}</span>
                        )}
                      </td>
                      <td className="cc-num"><Fee amount={c.monthly_fee} entered={c.monthly_fee_entered} /></td>
                      <td className="cc-muted cc-num"
                        title={c.started_month ? `נוצר במערכת ב-${formatDate(c.created_at)}` : undefined}>
                        {c.started_month ? monthLabelOf(c.started_month) : formatDate(c.created_at)}
                      </td>
                      <td><Managers emails={c.managers} /></td>
                      <td>{c.contact_name || <span className="cc-muted">—</span>}</td>
                      <td style={{ width: 36, textAlign: 'center' }}>
                        <RowMenu items={menuItems(c)} label={`פעולות עבור ${c.name}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </LoadState>

      {/* Said once, at the foot of the page (owner, 14/09/2026 — it crowded the
          period control), whenever the span reaches back before go-live: the
          hatched months in every chart above are the same fact, and the
          legend on each of them already points at it. */}
      {data && isPrelaunch(liveFrom, data.from) && (
        <p className="cc-prelaunch-note">
          <i className="cc-legend-dot is-prelaunch" />
          <span>PEAK עלתה לאוויר ב{monthLabelOf(liveFrom)} — החודשים שלפני כן בטווח לא נספרים באף מדד.</span>
        </p>
      )}

      {/* Both re-read the board on save rather than patching a row in place: a
          payment changes four headline figures and a trend bar, and a screen
          where the row agrees with itself but not with the totals above it is
          worse than one that took another 30ms. */}
      {editing?.kind === 'details' && (
        <CustomerDetailsModal
          customer={editing.customer}
          onSaved={() => { setEditing(null); load(); }}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.kind === 'headcount' && (
        <HeadcountModal
          onSaved={() => { setEditing(null); load(); }}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.kind === 'charge' && (
        <ChargeModal
          customer={editing.customer}
          month={defaultChargeMonth}
          // FOLLOW THE SAVE. The editor's month picker exists to record a month
          // you are not looking at, so a charge written outside the selected
          // span would otherwise land on a board that does not change — the
          // dialog closes over an unmoved page and the save reads as failed.
          // Widening the range to include the saved month rather than replacing
          // it keeps the period the user chose.
          onSaved={(savedMonth) => {
            setEditing(null);
            const covered = !savedMonth || (savedMonth >= data.from && savedMonth <= data.to);
            if (covered) { load(); return; }
            // Through changeRange, so the URL follows the widened period too.
            changeRange(
              savedMonth < data.from ? `${savedMonth}-01` : range.from,
              savedMonth > data.to ? lastDayOfMonth(savedMonth) : range.to,
            );
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
